import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Loader2, ExternalLink, Move } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MoveFolderPopover } from "./MoveFolderPopover";

interface GalleryImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageId: string;
  patientId: string;
  onUpdate?: () => void;
}

export function GalleryImageModal({
  isOpen,
  onClose,
  imageId,
  patientId,
  onUpdate
}: GalleryImageModalProps) {
  const [imageData, setImageData] = useState<{
    id: string;
    file_name: string;
    file_path: string;
    folder: string | null;
  } | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    if (isOpen && imageId) {
      loadImageData();
    } else if (!isOpen) {
      setImageData(null);
      setImageUrl(null);
    }
  }, [isOpen, imageId]);

  async function loadImageData() {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("patient_gallery")
        .select("id, file_name, file_path, folder")
        .eq("id", imageId)
        .single();

      if (error) throw error;

      if (data) {
        setImageData(data);

        // Carregar URL da imagem
        const { data: urlData, error: urlError } = await supabase.storage
          .from("patient-docs")
          .createSignedUrl(data.file_path, 3600);

        if (urlError) {
          console.error("Error loading image URL:", urlError);
        } else if (urlData?.signedUrl) {
          setImageUrl(urlData.signedUrl);
        }
      }
    } catch (error) {
      console.error("Error loading image data:", error);
      toast.error("Erro ao carregar dados da imagem");
    } finally {
      setLoading(false);
    }
  }

  function handleOpenImage() {
    if (imageUrl) {
      window.open(imageUrl, "_blank");
    }
  }

  async function handleMoveImage(newFolder: string | null) {
    if (!imageData) return;

    try {
      setIsMoving(true);
      // @ts-ignore - patient_gallery table not yet in types
      const { error } = await supabase
        .from("patient_gallery")
        .update({ folder: newFolder })
        .eq("id", imageData.id);

      if (error) throw error;

      toast.success("Imagem movida!");
      loadImageData();
      onUpdate?.();
    } catch (error) {
      console.error("Erro ao mover imagem:", error);
      toast.error("Erro ao mover imagem");
    } finally {
      setIsMoving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] w-[95vw] sm:w-full p-0 overflow-hidden">
        <div className="relative w-full h-[90vh] bg-muted flex items-center justify-center">
          {loading ? (
            <div className="flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : imageData && imageUrl ? (
            <>
              <img
                src={imageUrl}
                alt={imageData.file_name}
                className="max-w-full max-h-full w-auto h-auto object-contain"
                onError={(e) => {
                  console.error("Error loading image:", imageData.file_path);
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="absolute top-4 left-4 flex gap-2 z-10">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenImage}
                  className="gap-2 bg-background/90 backdrop-blur-sm shadow-lg"
                >
                  <ExternalLink className="h-4 w-4" />
                  <span className="hidden sm:inline">Abrir em Nova Aba</span>
                  <span className="sm:hidden">Abrir</span>
                </Button>
                <MoveFolderPopover
                  patientId={patientId}
                  currentFolder={imageData?.folder || null}
                  onSelectFolder={(folder) => {
                    handleMoveImage(folder);
                  }}
                  trigger={
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 bg-background/90 backdrop-blur-sm shadow-lg"
                    >
                      <Move className="h-4 w-4" />
                      <span className="hidden sm:inline">Mover</span>
                    </Button>
                  }
                />
              </div>
            </>
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              Imagem não encontrada
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

